/**
 * <mrk-wave data-mrk-wave-id="<product id>" data-mrk-wave-peaks="<custom.waveform>" data-mrk-wave-src="<mp3 url>">:
 * static Framer-style row waveform drawn from the beat's product field **custom.waveform** ("duration|base64 bars"),
 * so no audio is downloaded to draw it. The player reads the same field (beat JSON "wave") through window.mrkPeaks.
 * A beat whose field is still empty gets its waveform built once in the browser from the MP3 (only when on screen,
 * one at a time) and cached in localStorage. Fill the field with .claude/docs/waveform-tool.html.
 * The played part turns accent-red for the beat loaded in <mrk-player> (MRK_EVENTS.playerState), and any
 * [data-mrk-duration-for="<id>"] gets the track length.
 */
import { MRK_EVENTS, on } from './mrk-events.js';

const SVG_NS = 'http://www.w3.org/2000/svg';
const HEIGHT = 100;
const MIN_BAR = 5;

const BARS = 128;
const CACHE_PREFIX = 'mrk:peaks:v1:';

/** @param {{ d: number, p: string }} entry */
function decodeEntry(entry) {
  const bytes = Uint8Array.from(atob(entry.p), (char) => char.charCodeAt(0));
  return { duration: entry.d, peaks: Float32Array.from(bytes, (value) => value / 255) };
}

/** @param {string} src */
function fileKey(src) {
  try {
    return new URL(src, location.href).pathname.split('/').pop() || src;
  } catch {
    return src;
  }
}

/** @param {string} key */
function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** @param {string} key @param {{ d: number, p: string }} value */
function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked: the waveform is rebuilt next visit */
  }
}

/** Builds 128 RMS bars + duration from an MP3, same method as .claude/scripts/mrk-peaks.mjs. */
async function computeEntry(src) {
  const response = await fetch(src);
  if (!response.ok) throw new Error(String(response.status));
  const buffer = await response.arrayBuffer();
  const context = new OfflineAudioContext(1, 1, 44100);
  const audio = await context.decodeAudioData(buffer);
  const channels = Array.from({ length: audio.numberOfChannels }, (_, i) => audio.getChannelData(i));
  const total = audio.length;
  const bucket = Math.max(1, Math.floor(total / BARS));
  const levels = new Float64Array(BARS);
  for (let bar = 0; bar < BARS; bar++) {
    const end = Math.min(total, (bar + 1) * bucket);
    let sum = 0;
    let count = 0;
    for (let i = bar * bucket; i < end; i += 4) {
      let sample = 0;
      for (const channel of channels) sample += channel[i];
      sample /= channels.length;
      sum += sample * sample;
      count++;
    }
    levels[bar] = count ? Math.sqrt(sum / count) : 0;
  }
  const max = Math.max(...levels) || 1;
  let binary = '';
  for (const level of levels) binary += String.fromCharCode(Math.round((level / max) * 255));
  return { d: Math.round(audio.duration * 10) / 10, p: btoa(binary) };
}

/** Waveforms read from product fields (custom.waveform) on this page, by product id; the player looks here too. */
const fieldPeaks = new Map();

/**
 * Parses a custom.waveform value: "duration|base64" (from the waveform tool) or {"d":…,"p":"…"} JSON.
 * @param {string | undefined} value
 */
function parseField(value) {
  const text = (value || '').trim();
  if (!text) return null;
  try {
    if (text.startsWith('{')) {
      const json = JSON.parse(text);
      return json.p ? { d: Number(json.d) || 0, p: String(json.p) } : null;
    }
    const [d, p] = text.split('|');
    return p ? { d: Number(d) || 0, p: p.trim() } : null;
  } catch {
    return null;
  }
}

/** One MP3 at a time, so a page full of new beats never downloads them all at once. */
let computeQueue = Promise.resolve();

/**
 * The product field first (given here or seen on a row), then this visitor's cache, then (only when `src` is given)
 * build them from the MP3.
 * @param {string | number} id
 * @param {string} [src]
 * @param {string} [fieldValue] - raw custom.waveform value
 * @returns {Promise<{ duration: number, peaks: Float32Array } | null>}
 */
async function getPeaks(id, src, fieldValue) {
  const given = parseField(fieldValue);
  if (given) fieldPeaks.set(String(id), given);
  const field = fieldPeaks.get(String(id));
  if (field) {
    try {
      return decodeEntry(field);
    } catch {
      fieldPeaks.delete(String(id));
    }
  }
  const cacheKey = src ? `${CACHE_PREFIX}${id}:${fileKey(src)}` : null;
  if (cacheKey) {
    const cached = readCache(cacheKey);
    if (cached) return decodeEntry(cached);
  } else {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(`${CACHE_PREFIX}${id}:`)) {
          const cached = readCache(key);
          if (cached) return decodeEntry(cached);
        }
      }
    } catch {
      /* storage blocked */
    }
    return null;
  }

  const job = computeQueue.then(() => computeEntry(/** @type {string} */ (src)));
  computeQueue = job.catch(() => {});
  try {
    const entry = await job;
    writeCache(cacheKey, entry);
    return decodeEntry(entry);
  } catch {
    return null;
  }
}

/** @param {number} seconds */
function formatTime(seconds) {
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** @param {Float32Array} peaks */
function barsPath(peaks) {
  let d = '';
  for (let i = 0; i < peaks.length; i++) {
    const height = Math.max(MIN_BAR, peaks[i] * HEIGHT);
    d += `M${i * 2} ${HEIGHT}h1.2v${-height.toFixed(1)}h-1.2z`;
  }
  return d;
}

/** @param {string} d @param {number} width @param {string} className */
function svg(d, width, className) {
  const element = document.createElementNS(SVG_NS, 'svg');
  element.setAttribute('viewBox', `0 0 ${width} ${HEIGHT}`);
  element.setAttribute('preserveAspectRatio', 'none');
  element.setAttribute('class', className);
  element.setAttribute('focusable', 'false');
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', d);
  element.append(path);
  return element;
}

/** @type {Set<MrkWave>} */
const waves = new Set();
/** @type {{ id: string | null, ratio: number }} */
const current = { id: null, ratio: 0 };

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      observer.unobserve(entry.target);
      /** @type {MrkWave} */ (entry.target).draw();
    }
  },
  { rootMargin: '400px 0px' }
);

class MrkWave extends HTMLElement {
  #drawn = false;

  connectedCallback() {
    const field = parseField(this.dataset.mrkWavePeaks);
    if (field && this.dataset.mrkWaveId) fieldPeaks.set(this.dataset.mrkWaveId, field);
    waves.add(this);
    this.#applyProgress();
    if (!this.#drawn) observer.observe(this);
  }

  disconnectedCallback() {
    waves.delete(this);
    observer.unobserve(this);
  }

  async draw() {
    if (this.#drawn) return;
    this.#drawn = true;
    const id = this.dataset.mrkWaveId;
    const data = id ? await getPeaks(id, this.dataset.mrkWaveSrc) : null;
    if (!data) {
      this.classList.add('mrk-wave--empty');
      return;
    }
    const d = barsPath(data.peaks);
    const width = data.peaks.length * 2;
    this.replaceChildren(svg(d, width, 'mrk-wave__bars'), svg(d, width, 'mrk-wave__bars mrk-wave__bars--played'));
    this.classList.add('mrk-wave--ready');
    for (const slot of document.querySelectorAll(`[data-mrk-duration-for="${id}"]`)) {
      if (!slot.textContent?.trim()) slot.textContent = formatTime(data.duration);
    }
  }

  #applyProgress() {
    const ratio = this.dataset.mrkWaveId === current.id ? current.ratio : 0;
    this.style.setProperty('--mrk-wave-progress', String(ratio));
  }

  static update() {
    for (const wave of waves) wave.#applyProgress();
  }
}

if (!customElements.get('mrk-wave')) {
  customElements.define('mrk-wave', MrkWave);

  on(MRK_EVENTS.playerState, (event) => {
    const { id, currentTime, duration } = event.detail || {};
    current.id = id == null ? null : String(id);
    current.ratio = duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;
    MrkWave.update();
  });

  window.mrkPeaks = { get: getPeaks };
}
